/**
 * Invoices tab — HandOff invoices and self-billed invoices in one table.
 *
 * Both are VAT invoices for one billing entity and one period; the view switch narrows to one
 * type. Payment is a SETTLEMENT fact (two-supply-netting spec §6): each row shows the statement
 * that carries the document and that statement's status, never a per-invoice payment status.
 * Cash is recorded in the Settlements tab.
 *
 * Volumes are about a dozen documents a month, so the "All" view merges the two lists in the
 * browser (up to 100 of each); a combined endpoint is only worth it if that changes.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Grid,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Link,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Cancel as CancelIcon,
  CheckCircleOutline as AcceptIcon,
  ReportProblemOutlined as RejectIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { invoiceApi, selfBillApi } from '../../services/invoicingApi';
import { billingEntityApi } from '../../services/api';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';

const FETCH_LIMIT = 100;

const settlementColors = {
  issued: 'info',
  disputed: 'error',
  settled: 'success',
  cancelled: 'default',
};

const reviewColors = {
  pending: 'default',
  accepted: 'success',
  rejected: 'error',
};

const fmtDate = (d) => (d ? format(new Date(d), 'MMM d, yyyy') : '-');
const fmtPeriod = (a, b) => (a && b ? `${format(new Date(a), 'MMM d, yyyy')} – ${format(new Date(b), 'MMM d, yyyy')}` : '-');
const money = (v, currency = 'EUR') =>
  v === null || v === undefined
    ? '-'
    : `${currency} ${Number(v).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** One row shape for both document types. */
function normaliseInvoice(i) {
  return {
    kind: 'invoice',
    key: `inv-${i.id}`,
    id: i.id,
    number: i.invoice_number,
    entityName: i.customer_company_name,
    periodStart: i.supply_period_start,
    periodEnd: i.supply_period_end,
    issueDate: i.issue_date,
    subtotal: i.subtotal_excl_vat,
    vat: i.vat_amount,
    total: i.total_amount,
    currency: i.currency || 'EUR',
    documentStatus: i.document_status,
    reviewStatus: null,
    rejectionWindowEndsAt: null,
    settlementId: i.settlement_id,
    statementNumber: i.statement_number,
    settlementStatus: i.settlement_status,
    settlementDueDate: i.settlement_due_date,
    isOverdue: i.is_overdue,
  };
}

function normaliseSelfBill(s) {
  return {
    kind: 'self_bill',
    key: `sb-${s.id}`,
    id: s.id,
    number: s.self_bill_number,
    entityName: s.supplier_company_name,
    periodStart: s.supply_period_start,
    periodEnd: s.supply_period_end,
    issueDate: s.issue_date,
    subtotal: s.subtotal_excl_vat,
    vat: s.vat_amount,
    total: s.total_amount,
    currency: s.currency || 'EUR',
    documentStatus: s.document_status,
    reviewStatus: s.review_status,
    rejectionWindowEndsAt: s.rejection_window_ends_at,
    settlementId: s.settlement_id,
    statementNumber: s.statement_number,
    settlementStatus: s.settlement_status,
    settlementDueDate: s.settlement_due_date,
    isOverdue: s.is_overdue,
  };
}

function SettlementCell({ row, onOpen }) {
  if (!row.statementNumber) {
    return <Typography variant="caption" color="text.secondary">no statement</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      <Tooltip title="Open in Settlements">
        <Link component="button" variant="body2" onClick={() => onOpen && onOpen(row.statementNumber)}
          sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {row.statementNumber}
        </Link>
      </Tooltip>
      <Chip label={row.settlementStatus} color={settlementColors[row.settlementStatus] || 'default'} size="small" />
      {row.isOverdue && <Chip label="overdue" color="error" variant="outlined" size="small" />}
    </Box>
  );
}

export default function InvoiceList({ onOpenSettlement, focusStatement, onClearFocus }) {
  const [view, setView] = useState('all'); // all | invoice | self_bill
  const [rows, setRows] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [testMode, setTestMode] = useState(true); // default to test mode for safety

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [entities, setEntities] = useState([]);
  const [filterEntity, setFilterEntity] = useState('');
  const [filterSettlement, setFilterSettlement] = useState('');
  const [filterDocument, setFilterDocument] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  // Dialogs
  const [pdf, setPdf] = useState({ open: false, url: null, loading: false, row: null });
  const [detail, setDetail] = useState({ open: false, loading: false, row: null, data: null });
  const [cancelRow, setCancelRow] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const common = { limit: FETCH_LIMIT, offset: 0, test_mode: testMode };
      if (filterSettlement) common.settlement_status = filterSettlement;
      if (filterDocument) common.document_status = filterDocument;
      if (filterFrom) common.from_date = filterFrom;
      if (filterTo) common.to_date = filterTo;

      const wantInvoices = view !== 'self_bill';
      const wantSelfBills = view !== 'invoice';
      const [inv, sb] = await Promise.all([
        wantInvoices
          ? invoiceApi.list({ ...common, customer_billing_entity_id: filterEntity || undefined })
          : Promise.resolve({ invoices: [], total: 0 }),
        wantSelfBills
          ? selfBillApi.list({ ...common, supplier_billing_entity_id: filterEntity || undefined })
          : Promise.resolve({ self_bills: [], total: 0 }),
      ]);
      const merged = [
        ...(inv.invoices || []).map(normaliseInvoice),
        ...(sb.self_bills || []).map(normaliseSelfBill),
      ].sort((a, b) => (b.issueDate || '').localeCompare(a.issueDate || '') || (b.number || '').localeCompare(a.number || ''));
      setRows(merged);
      setTruncated((inv.total || 0) > FETCH_LIMIT || (sb.total || 0) > FETCH_LIMIT);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [view, testMode, filterEntity, filterSettlement, filterDocument, filterFrom, filterTo]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    setPage(0);
  }, [view, filterEntity, filterSettlement, filterDocument, filterFrom, filterTo, focusStatement]);

  useEffect(() => {
    if (showFilters && entities.length === 0) {
      billingEntityApi.list()
        .then((data) => setEntities(data.entities || []))
        .catch((err) => console.error('Failed to fetch billing entities:', err));
    }
  }, [showFilters, entities.length]);

  const visible = useMemo(
    () => (focusStatement ? rows.filter((r) => r.statementNumber === focusStatement) : rows),
    [rows, focusStatement],
  );
  const pageRows = visible.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const counts = useMemo(() => ({
    invoice: rows.filter((r) => r.kind === 'invoice').length,
    self_bill: rows.filter((r) => r.kind === 'self_bill').length,
  }), [rows]);

  // ------------------------------------------------------------------ PDFs
  const loadPdf = (row) =>
    row.kind === 'invoice'
      ? invoiceApi.downloadPdf(row.id, { test_mode: testMode })
      : selfBillApi.downloadPdf(row.id, { test_mode: testMode });

  const handleViewPdf = async (row) => {
    setPdf({ open: true, url: null, loading: true, row });
    try {
      const blob = await loadPdf(row);
      setPdf({ open: true, url: URL.createObjectURL(blob), loading: false, row });
    } catch (err) {
      setPdf({ open: true, url: null, loading: false, row });
      setError(`Failed to load PDF: ${err.message}`);
    }
  };

  const closePdf = () => {
    if (pdf.url) URL.revokeObjectURL(pdf.url);
    setPdf({ open: false, url: null, loading: false, row: null });
  };

  const handleDownloadPdf = async (row) => {
    try {
      const blob = await loadPdf(row);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${row.number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Failed to download PDF: ${err.message}`);
    }
  };

  // ------------------------------------------------------------------ detail
  const handleViewDetails = async (row) => {
    setDetail({ open: true, loading: true, row, data: null });
    try {
      const data = row.kind === 'invoice'
        ? await invoiceApi.get(row.id, { test_mode: testMode })
        : await selfBillApi.get(row.id, { test_mode: testMode });
      setDetail({ open: true, loading: false, row, data: data.self_bill || data });
    } catch (err) {
      setDetail({ open: true, loading: false, row, data: null });
      setError(err.message);
    }
  };

  // ------------------------------------------------------------------ actions
  const canCancel = (row) => row.kind === 'invoice' && row.documentStatus === 'issued' && row.settlementStatus !== 'settled';
  const canReview = (row) => row.kind === 'self_bill' && row.documentStatus === 'issued' && row.reviewStatus === 'pending';

  const handleConfirmCancel = async () => {
    setBusy(true);
    try {
      await invoiceApi.cancel(cancelRow.id, { reason: cancelReason || undefined, test_mode: testMode });
      setCancelRow(null);
      fetchRows();
    } catch (err) {
      setError(err.body?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleAccept = async (row) => {
    if (!window.confirm(`Record ${row.entityName}'s explicit acceptance of ${row.number}?`)) return;
    try {
      await selfBillApi.accept(row.id, { test_mode: testMode });
      setNotice(`${row.number} accepted`);
      fetchRows();
    } catch (err) {
      setError(err.body?.error || err.message);
    }
  };

  const handleConfirmReject = async () => {
    setBusy(true);
    try {
      await selfBillApi.reject(rejectRow.id, { reason: rejectReason.trim(), test_mode: testMode });
      setNotice(`${rejectRow.number} rejected; ${rejectRow.statementNumber || 'its statement'} is now disputed`);
      setRejectRow(null);
      fetchRows();
    } catch (err) {
      setError(err.body?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const clearFilters = () => {
    setFilterEntity('');
    setFilterSettlement('');
    setFilterDocument('');
    setFilterFrom('');
    setFilterTo('');
  };

  const kindChip = (row) => (
    <Chip
      label={row.kind === 'invoice' ? 'HandOff' : 'Self-billed'}
      color={row.kind === 'invoice' ? 'primary' : 'secondary'}
      variant="outlined"
      size="small"
    />
  );

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6">Invoices</Typography>
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, v) => v && setView(v)}>
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="invoice">HandOff invoices</ToggleButton>
            <ToggleButton value="self_bill">Self-billed</ToggleButton>
          </ToggleButtonGroup>
          <FormControlLabel
            control={<Switch checked={testMode} onChange={(e) => setTestMode(e.target.checked)} color="warning" />}
            label={<Chip label={testMode ? 'TEST MODE' : 'PRODUCTION'} color={testMode ? 'warning' : 'error'} size="small" />}
          />
        </Box>
        <Box>
          <Button variant="outlined" startIcon={<FilterIcon />} onClick={() => setShowFilters(!showFilters)} sx={{ mr: 1 }}>
            Filters
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchRows} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {focusStatement && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={onClearFocus}>
          Showing the documents of statement <strong>{focusStatement}</strong>.
        </Alert>
      )}
      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          More than {FETCH_LIMIT} documents of one type match. Narrow the filters to see them all.
        </Alert>
      )}

      {showFilters && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel shrink>Billing entity</InputLabel>
                <Select value={filterEntity} label="Billing entity" displayEmpty notched
                  onChange={(e) => setFilterEntity(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {entities.map((e) => <MenuItem key={e.id} value={e.id}>{e.legal_name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel shrink>Settlement</InputLabel>
                <Select value={filterSettlement} label="Settlement" displayEmpty notched
                  onChange={(e) => setFilterSettlement(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="issued">Issued</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                  <MenuItem value="disputed">Disputed</MenuItem>
                  <MenuItem value="settled">Settled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel shrink>Document</InputLabel>
                <Select value={filterDocument} label="Document" displayEmpty notched
                  onChange={(e) => setFilterDocument(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="issued">Issued</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField label="Issued from" type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)}
                InputLabelProps={{ shrink: true }} size="small" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField label="Issued until" type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)}
                InputLabelProps={{ shrink: true }} size="small" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Button variant="text" onClick={clearFilters}>Clear</Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Number</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Period</TableCell>
              <TableCell align="right">Excl. VAT</TableCell>
              <TableCell align="right">VAT</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Document</TableCell>
              <TableCell>Settlement</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row) => (
              <TableRow key={row.key} hover sx={row.documentStatus === 'cancelled' ? { opacity: 0.55 } : undefined}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 'bold' }}>{row.number}</TableCell>
                <TableCell>{kindChip(row)}</TableCell>
                <TableCell>{row.entityName}</TableCell>
                <TableCell>{fmtPeriod(row.periodStart, row.periodEnd)}</TableCell>
                <TableCell align="right">{money(row.subtotal, row.currency)}</TableCell>
                <TableCell align="right">{money(row.vat, row.currency)}</TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">{money(row.total, row.currency)}</Typography></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip label={row.documentStatus} size="small" variant={row.documentStatus === 'cancelled' ? 'outlined' : 'filled'} />
                    {row.kind === 'self_bill' && row.reviewStatus && row.reviewStatus !== 'pending' && (
                      <Chip label={row.reviewStatus} color={reviewColors[row.reviewStatus]} size="small" variant="outlined" />
                    )}
                  </Box>
                  {canReview(row) && row.rejectionWindowEndsAt && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      objection window until {fmtDate(row.rejectionWindowEndsAt)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell><SettlementCell row={row} onOpen={onOpenSettlement} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Details">
                    <IconButton size="small" onClick={() => handleViewDetails(row)}><ViewIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Preview PDF">
                    <IconButton size="small" onClick={() => handleViewPdf(row)}><PdfIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Download PDF">
                    <IconButton size="small" onClick={() => handleDownloadPdf(row)}><DownloadIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  {canCancel(row) && (
                    <Tooltip title="Cancel invoice">
                      <IconButton size="small" color="error" onClick={() => { setCancelRow(row); setCancelReason(''); }}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canReview(row) && (
                    <>
                      <Tooltip title="Record acceptance">
                        <IconButton size="small" color="success" onClick={() => handleAccept(row)}><AcceptIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Record rejection (disputes the statement)">
                        <IconButton size="small" color="error" onClick={() => { setRejectRow(row); setRejectReason(''); }}>
                          <RejectIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>No documents found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pl: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {view === 'all' ? `${counts.invoice} HandOff invoices · ${counts.self_bill} self-billed` : ''}
          </Typography>
          <TablePagination
            component="div"
            count={visible.length}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Box>
      </TableContainer>

      {/* PDF preview */}
      <Dialog open={pdf.open} onClose={closePdf} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle>
          {pdf.row?.kind === 'self_bill' ? 'Self-billed invoice' : 'Invoice'}
          {pdf.row && (
            <Typography component="span" variant="subtitle2" color="text.secondary" sx={{ display: 'block' }}>
              {pdf.row.number}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {pdf.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
          ) : pdf.url ? (
            <iframe src={pdf.url} width="100%" height="100%" style={{ border: 'none' }} title="PDF preview" />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="text.secondary">Failed to load PDF</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePdf}>Close</Button>
          {pdf.row && (
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => handleDownloadPdf(pdf.row)}>Download</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Detail */}
      <Dialog open={detail.open} onClose={() => setDetail({ ...detail, open: false })} maxWidth="md" fullWidth>
        <DialogTitle>
          {detail.row?.kind === 'self_bill' ? 'Self-billed invoice' : 'Invoice'} {detail.row?.number}
          {detail.row && <Box component="span" sx={{ ml: 2 }}>{kindChip(detail.row)}</Box>}
        </DialogTitle>
        <DialogContent>
          {detail.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : detail.row ? (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {detail.row.kind === 'self_bill' ? 'Supplier (issued on its behalf)' : 'Customer'}
                  </Typography>
                  <Typography variant="h6">{detail.row.entityName}</Typography>
                  {(detail.row.kind === 'self_bill' ? detail.data?.supplier_address : detail.data?.customer_address) && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                      {detail.row.kind === 'self_bill' ? detail.data.supplier_address : detail.data.customer_address}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Issued {fmtDate(detail.row.issueDate)}
                    <br />
                    Period {fmtPeriod(detail.row.periodStart, detail.row.periodEnd)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Settlement</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <SettlementCell row={detail.row} onOpen={(n) => { setDetail({ ...detail, open: false }); onOpenSettlement && onOpenSettlement(n); }} />
                  </Box>
                  {detail.row.settlementDueDate && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Statement due {fmtDate(detail.row.settlementDueDate)}
                    </Typography>
                  )}
                  {detail.row.kind === 'self_bill' && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Review: <strong>{detail.row.reviewStatus}</strong>
                      {detail.row.rejectionWindowEndsAt && <> · objection window until {fmtDate(detail.row.rejectionWindowEndsAt)}</>}
                    </Typography>
                  )}
                  {detail.data?.rejection_reason && (
                    <Alert severity="error" sx={{ mt: 1 }}>Rejected: {detail.data.rejection_reason}</Alert>
                  )}
                </Grid>
              </Grid>

              {(detail.data?.line_items || []).length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Line items</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow><TableCell>Description</TableCell><TableCell align="right">Amount</TableCell></TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.data.line_items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.description}</TableCell>
                            <TableCell align="right">{money(item.line_total, detail.row.currency)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Paper sx={{ p: 2, minWidth: { xs: 0, sm: 250 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Subtotal</Typography><Typography>{money(detail.row.subtotal, detail.row.currency)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>VAT{detail.data?.vat_rate !== undefined ? ` (${detail.data.vat_rate}%)` : ''}</Typography>
                    <Typography>{money(detail.row.vat, detail.row.currency)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: 1, borderColor: 'divider', pt: 1, mt: 1 }}>
                    <Typography fontWeight="bold">Total</Typography>
                    <Typography fontWeight="bold">{money(detail.row.total, detail.row.currency)}</Typography>
                  </Box>
                </Paper>
              </Box>
              {detail.data?.vat_note && <Alert severity="info" sx={{ mt: 2 }}>{detail.data.vat_note}</Alert>}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail({ ...detail, open: false })}>Close</Button>
          {detail.row && (
            <Button startIcon={<PdfIcon />} onClick={() => { const r = detail.row; setDetail({ ...detail, open: false }); handleViewPdf(r); }}>
              View PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel a HandOff invoice */}
      <Dialog open={!!cancelRow} onClose={() => setCancelRow(null)}>
        <DialogTitle>Cancel invoice</DialogTitle>
        <DialogContent>
          {cancelRow && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Cancelling <strong>{cancelRow.number}</strong> will:
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Mark the invoice as cancelled</li>
                  <li>Unlink all commission items (they can be invoiced again)</li>
                  <li>Allow a new invoice for the same period</li>
                </ul>
                {cancelRow.statementNumber && <>Statement <strong>{cancelRow.statementNumber}</strong> still refers to it; cancel or rerun that settlement too.<br /></>}
                This cannot be undone.
              </Alert>
              <TextField label="Reason (optional)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. wrong period, duplicate" fullWidth multiline rows={2} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelRow(null)}>Keep invoice</Button>
          <Button variant="contained" color="error" onClick={handleConfirmCancel} disabled={busy}
            startIcon={busy ? <CircularProgress size={16} /> : <CancelIcon />}>
            Cancel invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record a self-bill rejection */}
      <Dialog open={!!rejectRow} onClose={() => setRejectRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Record rejection</DialogTitle>
        <DialogContent>
          {rejectRow && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Record that <strong>{rejectRow.entityName}</strong> objected to <strong>{rejectRow.number}</strong> (agreement art. 6).
                Statement <strong>{rejectRow.statementNumber || '—'}</strong> becomes <strong>disputed</strong> and cannot be settled
                until the dispute is resolved in the Settlements tab.
              </Alert>
              <TextField label="Reason given by the retailer" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                fullWidth multiline rows={3} required />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectRow(null)}>Back</Button>
          <Button variant="contained" color="error" onClick={handleConfirmReject} disabled={busy || !rejectReason.trim()}
            startIcon={busy ? <CircularProgress size={16} /> : <RejectIcon />}>
            Record rejection
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
